import React from 'react';
import {
  AlertCircle,
  ArrowUp,
  Check,
  Loader2,
  Paperclip,
  MessageSquare,
  FileText,
  FolderOpen,
  X,
} from 'lucide-react';
import { ChatSessionListItem, Document, Project } from '../../../services/api';
import type { AttachedFileUpload } from '../../home/hooks/useHomeContextSelection';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { AskMode } from '../ChatShellContext';
import { AskModeSwitch } from './AskModeSwitch';
import { ASK_PLACEHOLDER } from '../constants';

const ASK_MODE_PLACEHOLDER: Record<AskMode, string | null> = {
  ai: null,
  staff: 'Ask the RCM team…',
  community: 'Ask the RCM community…',
};

export interface ChatInputProps {
  input: string;
  onInputChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  currentSessionRole: 'owner' | 'editor' | 'viewer' | null;
  sessionId?: string;
  loading: boolean;
  contextLocked: boolean;
  selectedDocs: Document[];
  selectedChats: ChatSessionListItem[];
  selectedProjects: Project[];
  attachedFiles: File[];
  /**
   * Optional parallel array describing the upload lifecycle of each attached
   * file. When provided, each pill renders a status indicator (spinner / check
   * / alert). When omitted (e.g. chat-page in-conversation attachments that
   * upload at submit time), the pill falls back to a plain Paperclip icon.
   */
  attachedFileUploads?: AttachedFileUpload[];
  /**
   * When true, the submit button is disabled and a helper message is shown —
   * used by the home page to prevent sending a message before a background
   * attachment upload finishes (issue #91). Defaults to false, so callers
   * that don't track upload state (chat-page mid-convo) are unaffected.
   */
  hasPendingUploads?: boolean;
  onRemoveDoc: (id: string) => void;
  onRemoveChat: (id: string) => void;
  onRemoveProject: (id: string) => void;
  onRemoveFile: (index: number) => void;
  uploadStatusLines: string[];
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  /** Focus the textarea on mount (empty chat entry). */
  autoFocus?: boolean;
  /** Tighter layout for the Library Ask RCM sidebar — no extra wrapper padding or nested card chrome. */
  variant?: 'default' | 'sidebar';
  /** When set, the composer can send to AI, staff, or the community. */
  askMode?: AskMode;
  /** Show AI / Staff / Community inside the composer toolbar. */
  showModes?: boolean;
  /** Empty Ask screen: taller composer, full width of the content column. */
  prominent?: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  input,
  onInputChange,
  onSubmit,
  onKeyDown,
  currentSessionRole,
  sessionId,
  loading,
  contextLocked,
  selectedDocs,
  selectedChats,
  selectedProjects,
  attachedFiles,
  attachedFileUploads,
  hasPendingUploads = false,
  onRemoveDoc,
  onRemoveChat,
  onRemoveProject,
  onRemoveFile,
  uploadStatusLines,
  fileInputRef,
  onFileSelect,
  placeholder,
  autoFocus = false,
  variant = 'default',
  askMode = 'ai',
  showModes = false,
  prominent = false,
}) => {
  const isSidebar = variant === 'sidebar';
  const humanAsk = askMode !== 'ai';
  const inputLocked = !humanAsk && currentSessionRole === 'viewer';

  const resolvedPlaceholder = inputLocked
    ? 'You have view-only access to this chat'
    : (placeholder ?? ASK_MODE_PLACEHOLDER[askMode] ?? ASK_PLACEHOLDER);
  const hasContextChips =
    currentSessionRole === 'viewer' ||
    (currentSessionRole === 'editor' && !!sessionId) ||
    selectedDocs.length > 0 ||
    selectedChats.length > 0 ||
    selectedProjects.length > 0 ||
    attachedFiles.length > 0;
  const sendLabel = loading
    ? 'Sending'
    : askMode === 'staff'
      ? 'Ask staff'
      : askMode === 'community'
        ? 'Ask community'
        : 'Send message';

  // Short-circuit both form submits and Enter-submit while an attachment is
  // still uploading — otherwise the user races ahead of the background
  // upload and the chat stream fires with no document_id (#91).
  const handleFormSubmit = (e: React.FormEvent) => {
    if (hasPendingUploads) {
      e.preventDefault();
      return;
    }
    onSubmit(e);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (hasPendingUploads && e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      return;
    }
    onKeyDown(e);
  };
  return (
  <div className={cn('flex-shrink-0', !isSidebar && !prominent && 'px-4 py-4')}>
    <div className={cn('w-full', !isSidebar && !prominent && 'mx-auto max-w-[720px]')}>
      <form onSubmit={handleFormSubmit} className="flex flex-col">
        <div
          className={cn(
            'ask-composer relative flex flex-col',
            isSidebar ? 'rounded-xl' : 'rounded-[24px]',
            prominent && 'min-h-[180px]',
          )}
        >
          <div className={cn('flex items-center gap-2 flex-wrap pb-2', isSidebar ? 'px-3 pt-2' : 'px-5 pt-3', (humanAsk || !hasContextChips) && 'hidden')}>
            {currentSessionRole === 'viewer' && (
              <Badge
                variant="secondary"
                className="gap-1 px-3 py-1.5 rounded-full border border-border"
              >
                <span className="text-xs">👁️ Viewing (read-only)</span>
              </Badge>
            )}
            {currentSessionRole === 'editor' && sessionId && (
              <Badge
                variant="secondary"
                className="gap-1 px-3 py-1.5 rounded-full border border-border"
              >
                <span className="text-xs">✏️ Editor</span>
              </Badge>
            )}

            {selectedDocs.map((doc) => (
              <Badge
                key={`doc-${doc.id}`}
                variant="secondary"
                className="gap-1 pl-3 pr-2 py-1.5 rounded-full"
              >
                <FileText className="h-3 w-3" />
                <span className="text-xs max-w-[150px] truncate">{doc.title}</span>
                {!contextLocked && (
                  <button
                    type="button"
                    onClick={() => onRemoveDoc(doc.id)}
                    className="hover:bg-background/50 rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </Badge>
            ))}

            {selectedChats.map((chat) => (
              <Badge
                key={`chat-${chat.id}`}
                variant="secondary"
                className="gap-1 pl-3 pr-2 py-1.5 rounded-full"
              >
                <MessageSquare className="h-3 w-3" />
                <span className="text-xs max-w-[150px] truncate">{chat.title}</span>
                {!contextLocked && (
                  <button
                    type="button"
                    onClick={() => onRemoveChat(chat.id)}
                    className="hover:bg-background/50 rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </Badge>
            ))}

            {selectedProjects.map((project) => (
              <Badge
                key={`project-${project.id}`}
                variant="secondary"
                className="gap-1 pl-3 pr-2 py-1.5 rounded-full"
              >
                <FolderOpen className="h-3 w-3" />
                <span className="text-xs max-w-[150px] truncate">{project.name}</span>
                {!contextLocked && (
                  <button
                    type="button"
                    onClick={() => onRemoveProject(project.id)}
                    className="hover:bg-background/50 rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </Badge>
            ))}

            {attachedFiles.map((file, idx) => {
              const upload = attachedFileUploads?.[idx];
              const status = upload?.status;
              let statusIcon: React.ReactNode;
              if (status === 'uploading') {
                statusIcon = (
                  <span title="Uploading…" aria-label="Uploading" className="flex">
                    <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                  </span>
                );
              } else if (status === 'ready') {
                statusIcon = (
                  <span title="Ready" aria-label="Ready" className="flex">
                    <Check className="h-3 w-3 text-green-600" />
                  </span>
                );
              } else if (status === 'error') {
                statusIcon = (
                  <span
                    title={upload?.error || 'Upload failed'}
                    aria-label="Upload failed"
                    className="flex"
                  >
                    <AlertCircle className="h-3 w-3 text-amber-600" />
                  </span>
                );
              } else {
                statusIcon = <Paperclip className="h-3 w-3" />;
              }
              return (
                <Badge
                  key={`file-${idx}`}
                  variant="secondary"
                  className="gap-1 pl-3 pr-2 py-1.5 rounded-full"
                >
                  {statusIcon}
                  <span
                    className={`text-xs max-w-[150px] truncate ${
                      status === 'error' ? 'text-amber-700' : ''
                    }`}
                  >
                    {file.name}
                  </span>
                  {!contextLocked && (
                    <button
                      type="button"
                      onClick={() => onRemoveFile(idx)}
                      className="hover:bg-background/50 rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </Badge>
              );
            })}
          </div>

          {uploadStatusLines.length > 0 && (
            <div className="px-5 pt-2 flex flex-col gap-0.5">
              {uploadStatusLines.map((line, i) => (
                <p
                  key={i}
                  className={`text-xs ${
                    line.startsWith('⚠') ? 'text-amber-600' : 'text-muted-foreground'
                  }`}
                >
                  {line}
                </p>
              ))}
            </div>
          )}

          <Textarea
            id="ask-composer"
            placeholder={resolvedPlaceholder}
            value={input}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            className={cn(
              'resize-none border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0',
              prominent
                ? 'min-h-[88px] flex-1 px-6 pt-5 text-base'
                : isSidebar
                  ? 'min-h-[48px] max-h-[100px] px-3 py-3 text-sm'
                  : 'min-h-[56px] max-h-[160px] px-5 pt-4 text-base',
            )}
            rows={prominent ? 3 : 2}
            disabled={inputLocked}
            autoFocus={autoFocus && !inputLocked}
          />

          {hasPendingUploads && (
            <div className="px-5 pb-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Loader2 className="h-3 w-3 animate-spin" />
                Waiting for attachment to finish uploading…
              </p>
            </div>
          )}

          <div className={cn('mt-auto flex items-end justify-between gap-2', isSidebar ? 'px-2 pb-2' : 'px-3 pb-3')}>
            <div className="flex min-w-0 items-center">
              {!humanAsk && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={onFileSelect}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.txt,.md"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => fileInputRef.current?.click()}
                    aria-label="Attach file"
                    className="h-11 w-11 text-[var(--rcm-color-text-secondary)] hover:bg-transparent hover:text-foreground"
                  >
                    <Paperclip className="h-4 w-4" strokeWidth={1.75} />
                  </Button>
                  {showModes && (
                    <span className="mx-1 h-5 w-px bg-[var(--rcm-color-border)]" aria-hidden />
                  )}
                </>
              )}
              {showModes && <AskModeSwitch />}
            </div>

            <Button
              type="submit"
              disabled={loading || !input.trim() || inputLocked || hasPendingUploads}
              title={hasPendingUploads ? 'Waiting for attachment to finish uploading…' : sendLabel}
              aria-label={sendLabel}
              size="icon"
              className="h-11 w-11 shrink-0 rounded-full bg-[var(--rcm-color-primary)] text-white hover:bg-[var(--rcm-color-primary)]/90 disabled:bg-[var(--rcm-color-primary)] disabled:opacity-45"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </form>
    </div>
  </div>
  );
};
